
import { getSupabase } from './supabaseClient';
import {
    getRedemptionItemsLocal,
    exportDataToJson
} from './storageService';
import {
    saveRedemptionItemsToDB
} from './db';
import { RedemptionItem } from '../types';
import { healthStorageService } from './healthStorageService';

const LAST_SYNC_KEY = 'habitsync_last_sync';
const SYNC_VERSION = '1.0.0';

// UUID v4/v7 compatible validator
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isValidUUID = (id?: string | null): boolean => !!id && UUID_REGEX.test(id);

// Helpers to transform keys (Camel <-> Snake)
const toSnakeCase = (obj: any): any => {
    if (Array.isArray(obj)) return obj.map(toSnakeCase);
    if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
            acc[snakeKey] = toSnakeCase(obj[key]);
            return acc;
        }, {} as any);
    }
    return obj;
};

const toCamelCase = (obj: any): any => {
    if (Array.isArray(obj)) return obj.map(toCamelCase);
    if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
            acc[camelKey] = toCamelCase(obj[key]);
            return acc;
        }, {} as any);
    }
    return obj;
};

export const getLastSyncTime = () => localStorage.getItem(LAST_SYNC_KEY);

export const syncData = async () => {
    const supabase = getSupabase();
    if (!supabase) throw new Error("Supabase not configured");

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("User not logged in");

    const lastSync = getLastSyncTime();
    const lastSyncDate = lastSync ? new Date(lastSync) : new Date(0);
    const syncStartTime = new Date(); // Capture sync start time to avoid race conditions

    console.log(`[Sync v${SYNC_VERSION}] Starting sync, last sync: ${lastSync || 'never'}`);

    // Helper to safely push data and log errors without breaking sync
    const safeUpsert = async (table: string, data: any[], label: string) => {
        if (data.length === 0) return 0;
        try {
            const { error } = await supabase.from(table).upsert(data as any);
            if (error) {
                console.warn(`[Sync] ${label} push skipped:`, error.message);
                return 0;
            }
            return data.length;
        } catch (e) {
            console.warn(`[Sync] ${label} push failed:`, e);
            return 0;
        }
    };

    // =====================================================================
    // PULL: Get redemption_items updates from Supabase since last sync
    // Also detect deletions (items on cloud that are newer than last sync but not in local)
    // =====================================================================
    const { data: remoteItems, error: errItems } = await supabase
        .from('redemption_items')
        .select('*')
        .gt('updated_at', lastSyncDate.toISOString());
    if (errItems) throw errItems;

    // Always fetch full list for deletion detection
    const { data: allRemoteItems, error: errAllRemote } = await supabase
        .from('redemption_items')
        .select('id, updated_at')
        .eq('user_id', user.id) as { data: { id: string, updated_at: string }[] | null, error: any };

    const currentItems = await getRedemptionItemsLocal();
    const currentItemIds = new Set(currentItems.map(i => i.id));

    // Detect cloud deletions: items that existed locally but are now gone from cloud
    // (only if we have cloud data to compare)
    if (!errAllRemote && allRemoteItems) {
        const remoteItemIds = new Set(allRemoteItems.map(r => r.id));
        const locallyDeletedItems = currentItems.filter(i =>
            !remoteItemIds.has(i.id) &&
            i.updatedAt && new Date(i.updatedAt) <= lastSyncDate // Only delete if local copy is older than last sync
        );

        if (locallyDeletedItems.length > 0) {
            console.log(`[Sync] Removing ${locallyDeletedItems.length} locally deleted items (synced from cloud)`);
            const remainingItems = currentItems.filter(i => !locallyDeletedItems.some(d => d.id === i.id));
            await saveRedemptionItemsToDB(remainingItems);
        }
    }

    // Merge updated items from cloud
    if (remoteItems && remoteItems.length > 0) {
        const mergedItems = mergeData(currentItems, remoteItems.map(toCamelCase));
        await saveRedemptionItemsToDB(mergedItems);
        console.log(`[Sync] Pulled ${remoteItems.length} updated items from cloud`);
    }

    // =====================================================================
    // PUSH: Send local redemption_items updates to Supabase
    // Also sync deletions by comparing local vs remote
    // =====================================================================
    const localItems = await getRedemptionItemsLocal();
    const changedItems = localItems.filter(i =>
        i.updatedAt && new Date(i.updatedAt) > lastSyncDate
        && isValidUUID(i.id)
    );
    const pushedCount = await safeUpsert('redemption_items', changedItems.map(i => ({ ...toSnakeCase(i), user_id: user.id })), 'Redemption Items');
    if (pushedCount > 0) {
        console.log(`[Sync] Pushed ${pushedCount} updated items to cloud`);
    }

    // Sync deletions: find items on Supabase that no longer exist locally
    // Only delete cloud items that are older than sync start time (avoid deleting items pushed by another device)
    if (!errAllRemote && allRemoteItems) {
        const localItemIds = new Set(localItems.map(i => i.id));
        const itemsToDelete = allRemoteItems
            .filter(r => !localItemIds.has(r.id) && new Date(r.updated_at) <= lastSyncDate)
            .map(r => r.id);

        if (itemsToDelete.length > 0) {
            try {
                const { error } = await supabase
                    .from('redemption_items')
                    .delete()
                    .in('id', itemsToDelete);
                if (error) {
                    console.warn('[Sync] Redemption Items delete skipped:', error.message);
                } else {
                    console.log(`[Sync] Deleted ${itemsToDelete.length} items from cloud (removed locally)`);
                }
            } catch (e) {
                console.warn('[Sync] Redemption Items delete failed:', e);
            }
        }
    }

    // Update Sync Time
    localStorage.setItem(LAST_SYNC_KEY, syncStartTime.toISOString());
    console.log(`[Sync] Sync completed successfully at ${syncStartTime.toISOString()}`);
};

// Merge Helper: Last Modified wins
const mergeData = (local: any[], remote: any[]) => {
    const map = new Map();
    local.forEach(i => map.set(i.id, i));
    remote.forEach(r => {
        const l = map.get(r.id);
        if (!l) {
            map.set(r.id, r);
        } else {
            const lTime = l.updatedAt ? new Date(l.updatedAt).getTime() : 0;
            const rTime = r.updatedAt ? new Date(r.updatedAt).getTime() : 0;
            if (rTime > lTime) {
                map.set(r.id, r);
            }
        }
    });
    return Array.from(map.values());
};

// --- Backup to File ---

export const uploadBackupFile = async () => {
    const supabase = getSupabase();
    if (!supabase) throw new Error("Supabase not configured");

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("User not logged in");

    // Generate JSON - includes all local data
    const { exportDataToJson } = await import('./storageService');
    const { getHabitsLocal, getMembersLocal, getRedemptionRecordsLocal } = await import('./storageService');

    const habits = await getHabitsLocal();
    const members = await getMembersLocal();
    const items = await getRedemptionItemsLocal();
    const records = await getRedemptionRecordsLocal();
    const healthProfiles = await healthStorageService.getProfiles();
    const healthRecords = await healthStorageService.getRecords();

    const backupData = {
        version: 6,
        syncVersion: SYNC_VERSION,
        exportedAt: new Date().toISOString(),
        habits,
        members,
        redemptionItems: items,
        redemptionRecords: records,
        healthProfiles,
        healthRecords
    };

    const blob = new Blob([JSON.stringify(backupData)], { type: 'application/json' });
    const fileName = `${user.id}/${new Date().toISOString().replace(/[:.]/g, '-')}.json`;

    const { error } = await supabase.storage
        .from('habit-backups')
        .upload(fileName, blob, { upsert: true });

    if (error) throw error;
    return fileName;
};

export const listBackups = async () => {
    const supabase = getSupabase();
    if (!supabase) return [];
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase.storage
        .from('habit-backups')
        .list(user.id + '/');

    if (error) throw error;
    return data;
};

export const restoreBackup = async (fileName: string) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error("Supabase config missing");
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("User not logged in");

    const { data, error } = await supabase.storage
        .from('habit-backups')
        .download(`${user.id}/${fileName}`);

    if (error) throw error;

    const text = await data.text();
    const json = JSON.parse(text);

    // Validate backup version
    if (json.version && json.version < 6) {
        console.warn(`[Restore] Backup version ${json.version} is older than current version 6`);
    }

    // Restore to local DB
    const { saveHabitsToDB, saveMembersToDB, saveRedemptionItemsToDB, saveRedemptionRecordsToDB } = await import('./db');

    if (json.habits) await saveHabitsToDB(json.habits);
    if (json.members) await saveMembersToDB(json.members);
    if (json.redemptionItems) await saveRedemptionItemsToDB(json.redemptionItems);
    if (json.redemptionRecords) await saveRedemptionRecordsToDB(json.redemptionRecords);

    // Restore health data
    if (json.healthProfiles && Array.isArray(json.healthProfiles)) {
        for (const profile of json.healthProfiles) {
            await healthStorageService.saveProfile(profile);
        }
    }
    if (json.healthRecords && Array.isArray(json.healthRecords)) {
        for (const record of json.healthRecords) {
            await healthStorageService.saveRecord(record);
        }
    }

    return true;
};

type SyncStatus = 'idle' | 'syncing' | 'success' | 'error';

const listeners: ((status: SyncStatus) => void)[] = [];

export const syncService = {
    subscribeToStatus: (callback: (status: SyncStatus) => void) => {
        listeners.push(callback);
        return () => {
            const index = listeners.indexOf(callback);
            if (index > -1) {
                listeners.splice(index, 1);
            }
        };
    },

    notifyStatus: (status: SyncStatus) => {
        listeners.forEach(listener => listener(status));
    },

    sync: async (userId: string): Promise<{ success: boolean }> => {
        try {
            syncService.notifyStatus('syncing');
            await syncData();
            syncService.notifyStatus('success');
            return { success: true };
        } catch (error) {
            syncService.notifyStatus('error');
            console.error('Sync failed:', error);
            return { success: false };
        }
    },

    triggerSync: (userId: string) => {
        syncService.sync(userId);
    },

    forceSync: async (userId: string): Promise<{ success: boolean }> => {
        try {
            syncService.notifyStatus('syncing');
            localStorage.removeItem(LAST_SYNC_KEY);
            await syncData();
            syncService.notifyStatus('success');
            return { success: true };
        } catch (error) {
            syncService.notifyStatus('error');
            console.error('Force sync failed:', error);
            return { success: false };
        }
    }
};
